import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../Layout/ResponsiveGrid';
import { Github, ExternalLink, Code, Loader, Calendar, Star, ArrowRight } from 'lucide-react';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const ProjectsSection = styled.section`
  padding: 6rem 0;
  position: relative;
  background: ${({ theme }) => `${theme.colors.accent}05`};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4rem 0;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 4px;
    background: ${({ theme }) => theme.colors.accent};
    border-radius: 2px;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 3rem;
  max-width: 600px;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: ${({ theme }) => theme.shadows.md};
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => `${0.1 * $index}s`};
  opacity: 0;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent};
    
    .project-image {
      transform: scale(1.05);
    }
    
    .project-title {
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.secondary};
`;

const ProjectImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg, 
    ${({ theme }) => theme.colors.secondary},
    ${({ theme }) => `${theme.colors.accent}20`}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.5s ease;
  
  svg {
    color: ${({ theme }) => theme.colors.accent};
    opacity: 0.7;
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  transition: color 0.3s ease;
  class: 'project-title';
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const ProjectTag = styled.span`
  background: ${({ theme }) => `${theme.colors.accent}15`};
  color: ${({ theme }) => theme.colors.accent};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ProjectFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.secondary};
  padding-top: 1rem;
  margin-top: auto;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme }) => `${theme.colors.accent}10`};
  color: ${({ theme }) => theme.colors.accent};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: translateY(-3px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  flex-direction: column;
  gap: 1rem;
  animation: ${fadeIn} 0.5s ease-out forwards;
`;

const LoadingSpinner = styled.div`
  svg {
    animation: ${rotate} 1.5s linear infinite;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const ErrorContainer = styled.div`
  background: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #b91c1c;
  animation: ${fadeIn} 0.5s ease-out forwards;
`;

const ErrorTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const ViewAllButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 3rem auto 0;
  padding: 0.75rem 2rem;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-decoration: none;
  width: fit-content;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background: ${({ theme }) => `${theme.colors.accent}e5`};
    
    svg {
      transform: translateX(3px);
    }
  }
  
  svg {
    transition: transform 0.3s ease;
  }
`;

const RedesignedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://api.github.com/users/pascalnd/repos?sort=updated&per_page=6');
        
        if (!response.ok) {
          throw new Error(`GitHub API Error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Filter out forked repositories and process the data
        const filteredProjects = data
          .filter(repo => !repo.fork && repo.description)
          .slice(0, 6)
          .map(repo => ({
            id: repo.id,
            title: repo.name,
            description: repo.description || 'No description available',
            language: repo.language,
            tags: [repo.language].filter(Boolean),
            repoUrl: repo.html_url,
            createdAt: repo.created_at,
            stars: repo.stargazers_count
          }));
        
        setProjects(filteredProjects);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects from GitHub. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <ProjectsSection id="projects">
        <Container>
          <SectionHeading>Projects</SectionHeading>
          <SectionSubheading>Here are some of my recent GitHub projects</SectionSubheading>
          
          <LoadingContainer>
            <LoadingSpinner>
              <Loader size={40} />
            </LoadingSpinner>
            <LoadingText>Loading projects from GitHub...</LoadingText>
          </LoadingContainer>
        </Container>
      </ProjectsSection>
    );
  }

  if (error) {
    return (
      <ProjectsSection id="projects">
        <Container>
          <SectionHeading>Projects</SectionHeading>
          <SectionSubheading>Here are some of my recent GitHub projects</SectionSubheading>
          
          <ErrorContainer>
            <ErrorTitle>Oops! Something went wrong</ErrorTitle>
            <ErrorMessage>{error}</ErrorMessage>
          </ErrorContainer>
        </Container>
      </ProjectsSection>
    );
  }

  // If we couldn't fetch real projects, use mock data
  const displayProjects = projects.length > 0 ? projects : [
    {
      id: 1,
      title: 'Clean C++ Library',
      description: 'A modern C++ library implementing clean code principles and test-driven development best practices',
      language: 'C++',
      tags: ['C++', 'TDD'],
      repoUrl: 'https://github.com',
      createdAt: '2024-01-15T00:00:00Z',
      stars: 12
    },
    {
      id: 2,
      title: 'Python Data Analyzer',
      description: 'Tool for analyzing and visualizing complex datasets with Python',
      language: 'Python',
      tags: ['Python', 'Data Analysis'],
      repoUrl: 'https://github.com',
      createdAt: '2024-02-20T00:00:00Z',
      stars: 8
    },
    {
      id: 3,
      title: 'Personal Portfolio',
      description: 'Modern responsive portfolio website built with React and styled-components',
      language: 'JavaScript',
      tags: ['React', 'JavaScript'],
      repoUrl: 'https://github.com',
      createdAt: '2024-03-05T00:00:00Z',
      stars: 5
    }
  ];

  return (
    <ProjectsSection id="projects">
      <Container>
        <SectionHeading>Projects</SectionHeading>
        <SectionSubheading>Here are some of my recent GitHub projects</SectionSubheading>
        
        <ProjectsGrid>
          {displayProjects.map((project, index) => (
            <ProjectCard key={project.id} $index={index}>
              <ProjectImageContainer>
                <ProjectImagePlaceholder className="project-image">
                  <Code size={40} />
                </ProjectImagePlaceholder>
              </ProjectImageContainer>
              
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                
                <ProjectTags>
                  {project.tags.map((tag, tagIndex) => (
                    tag && <ProjectTag key={tagIndex}>{tag}</ProjectTag>
                  ))}
                </ProjectTags>
                
                <ProjectFooter>
                  <ProjectMeta>
                    <MetaItem>
                      <Calendar size={16} />
                      {new Date(project.createdAt).toLocaleDateString()}
                    </MetaItem>
                    
                    <MetaItem>
                      <Star size={16} />
                      {project.stars}
                    </MetaItem>
                  </ProjectMeta>
                  
                  <ProjectLink 
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${project.title} on GitHub`}
                  >
                    <Github size={18} />
                  </ProjectLink>
                </ProjectFooter>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
        
        <ViewAllButton 
          href="https://github.com/pascalnd"
          target="_blank"
          rel="noopener noreferrer"
        >
          View All Projects
          <ArrowRight size={18} />
        </ViewAllButton>
      </Container>
    </ProjectsSection>
  );
};

export default RedesignedProjects;